/**
 * ESR LLC (Monday 13376550851, Jaron, Oct 23): "We need to add this as another
 * contractor just like MS Electric. It can say the same thing as MS but remove
 * the boxes and just write down that info like we had before."
 *
 * The company itself is a record in `Misc/env/contractCompanies/ESR` (same
 * node as MSE/CE; see `useContractCompany`). This file holds only what code
 * needs to know about it:
 * - its short name, so the report can route it to the M+S agreement;
 * - which companies sign through the M+S/CE agreement fields
 *   (`home_improvement_*`), so signing status, guided signing and the
 *   signed-contract email treat ESR the same as M+S;
 * - the contractor details the agreement prints, read from the Firebase
 *   record, with clearly marked placeholders for whatever Jaron has not given
 *   us yet. Never invent a licence, address or phone for a real contractor.
 */

export const ESR_SHORT_NAME = 'ESR'
export const ESR_LONG_NAME = 'ESR LLC'

/** Shown wherever ESR data is still missing. Data comes from Jaron. */
export const ESR_PLACEHOLDERS = {
  address: '[[PENDING: ESR LLC address]]',
  email: '[[PENDING: ESR LLC email]]',
  license: '[[PENDING: ESR LLC contractor license #]]',
  phone: '[[PENDING: ESR LLC phone]]',
} as const

const HOME_IMPROVEMENT_AGREEMENT_COMPANIES: ReadonlySet<string> = new Set([
  'MSE',
  'CE',
  ESR_SHORT_NAME,
])

/**
 * Companies whose agreement is `MSElectricContract` and whose signatures live
 * in the `home_improvement_*` fields.
 */
export const usesHomeImprovementAgreement = (
  company: string | null | undefined,
): boolean => HOME_IMPROVEMENT_AGREEMENT_COMPANIES.has(company || '')

type EsrRecord =
  | Readonly<{
      address1?: string
      address2?: string
      email?: string
      longName?: string
      phoneNumber?: string
      roofingLicense?: string
      website?: string
    }>
  | null
  | undefined

const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

/**
 * Contractor details for the ESR agreement (same shape as the
 * `ContractPartyInfo` MSElectricContract takes). A blank field in the
 * Firebase record falls back to its placeholder, so a missing licence prints
 * as visibly pending instead of as an empty line on a signed document.
 */
export const esrPartyInfo = (record: EsrRecord) => {
  const address2 = text(record?.address2)
  const website = text(record?.website)
  return {
    address1: text(record?.address1) || ESR_PLACEHOLDERS.address,
    ...(address2 ? { address2 } : {}),
    email: text(record?.email) || ESR_PLACEHOLDERS.email,
    license: text(record?.roofingLicense) || ESR_PLACEHOLDERS.license,
    name: text(record?.longName) || ESR_LONG_NAME,
    phone: text(record?.phoneNumber) || ESR_PLACEHOLDERS.phone,
    ...(website ? { website } : {}),
  }
}
